// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { Loader2, Save, Shield } from "lucide-react";
import { useNotification } from "../../context/NotificationContext";
import { tenantSettingsService } from "../../services/tenantSettingsService";
import { usePermissions } from "../../hooks/usePermissions";
import { useRoutes } from "../../hooks/useRoutes";
import { hasSuperAdminAccess, hasAdminAccess } from "../../config/roleConfig";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import useTenants from "../../hooks/useTenants";
import { getAdminTenantConfig } from "../../utils/tenantConfig";
import { emitTenantSettingsUpdated } from "../../utils/tenantSettingsEvents";
import PageLoader from "../../components/PageLoader";

const DEFAULT_AUTOMATION = {
  booking_reminders_enabled: true,
  booking_reminders_hours_before: 24,
  auto_cancel_unpaid_enabled: true,
  llm_evaluation_enabled: true,
  web_plausibility_enabled: true,
  reputation_check_enabled: false,
};

const toggleTrackClass =
  "w-11 h-6 bg-gray-200 dark:bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-color rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:after:bg-slate-300 after:border-gray-300 dark:after:border-slate-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-color dark:peer-checked:bg-primary-color/40";

const AutomatisierungSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ ...DEFAULT_AUTOMATION });
  const { showSuccess, showError } = useNotification();
  const { hasPermission } = usePermissions();
  const { userRoles, userInfo } = useRoutes();
  const isSuperAdmin = hasSuperAdminAccess(userRoles || []);
  const isAdmin = hasAdminAccess(userRoles || []);
  let selectednewTenant = null;
  if (isAdmin) {
    selectednewTenant = userInfo.tenant_id || null;
  }
  const {
    tenantOptions,
    selectedTenant,
    setSelectedTenant,
    loading: tenantsLoading,
  } = useTenants({ ...getAdminTenantConfig(isSuperAdmin), includeAll: false });

  const loadSettings = async () => {
    try {
      setLoading(true);
      if ((isSuperAdmin || isAdmin) && !selectedTenant && !selectednewTenant) {
        setSettings({ ...DEFAULT_AUTOMATION });
        return;
      }

      const tenantId =
        isSuperAdmin || isAdmin ? selectedTenant || selectednewTenant : null;
      if (!tenantId) {
        setSettings({ ...DEFAULT_AUTOMATION });
        return;
      }

      const config = await tenantSettingsService.getTenantConfig(tenantId);
      setSettings({
        ...DEFAULT_AUTOMATION,
        ...(config.automation || {}),
      });
    } catch (error) {
      console.error("Error loading automation settings:", error);
      showError("Fehler beim Laden der Automatisierungseinstellungen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, [selectedTenant, isSuperAdmin]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setSettings((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    if (type === "number") {
      const n = value === "" ? "" : parseInt(value, 10);
      setSettings((prev) => ({
        ...prev,
        [name]: Number.isNaN(n) ? prev[name] : n,
      }));
      return;
    }
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      if ((isSuperAdmin || isAdmin) && !selectedTenant && !selectednewTenant) {
        showError("Bitte zuerst einen Mandanten auswählen");
        return;
      }

      const tenantId =
        isSuperAdmin || isAdmin ? selectedTenant || selectednewTenant : null;
      if (!tenantId) {
        showError("Kein Mandant für das Speichern gefunden");
        return;
      }

      const currentConfig =
        await tenantSettingsService.getTenantConfig(tenantId);
      const nextConfig = {
        ...currentConfig,
        automation: {
          ...DEFAULT_AUTOMATION,
          ...settings,
          booking_reminders_hours_before:
            settings.booking_reminders_hours_before === ""
              ? DEFAULT_AUTOMATION.booking_reminders_hours_before
              : Number(settings.booking_reminders_hours_before) ||
                DEFAULT_AUTOMATION.booking_reminders_hours_before,
        },
      };

      await tenantSettingsService.updateTenantConfig(tenantId, nextConfig);
      emitTenantSettingsUpdated(tenantId);
      showSuccess("Automatisierungseinstellungen erfolgreich gespeichert!");
      await loadSettings();
    } catch (error) {
      console.error("Error saving automation settings:", error);
      showError("Fehler beim Speichern der Einstellungen");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveSettings();
  };

  if (
    !isSuperAdmin &&
    !isAdmin &&
    !hasPermission("tenant_settings.manage") &&
    !hasPermission("tenant_settings.view") &&
    !hasPermission("system.admin") &&
    !hasPermission("system.view")
  ) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-slate-400">
            Keine Berechtigung für diese Einstellungen
          </p>
        </div>
      </div>
    );
  }

  const hoursInputClass =
    "w-16 sm:w-20 px-2 py-1.5 text-sm text-center border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <GlobalAdminHeader
        title="Automatisierung"
        subtitle="Automatische Prozesse konfigurieren"
        tenantOptions={isSuperAdmin ? tenantOptions : []}
        selectedTenant={isSuperAdmin ? selectedTenant : null}
        onTenantChange={isSuperAdmin ? setSelectedTenant : undefined}
        tenantsLoading={isSuperAdmin ? tenantsLoading : false}
      />
      <div className="mx-auto px-4 lg:px-6 sm:mt-6 mt-3 pb-8">
        {loading ? (
          <PageLoader message="Lade Einstellungen..." />
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 sm:p-6 p-4 w-full">
            <form className="space-y-8" onSubmit={handleSubmit}>
              <section>
                <h3 className="text-base font-semibold text-gray-900 dark:text-slate-100 mb-4">
                  Erinnerungen
                </h3>
                <div className="space-y-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        name="booking_reminders_enabled"
                        checked={!!settings.booking_reminders_enabled}
                        onChange={handleChange}
                      />
                      <div className={toggleTrackClass} />
                    </label>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      Automatische Buchungserinnerungen
                    </span>
                    <input
                      type="number"
                      name="booking_reminders_hours_before"
                      min={1}
                      max={168}
                      disabled={!settings.booking_reminders_enabled}
                      value={
                        settings.booking_reminders_hours_before === ""
                          ? ""
                          : settings.booking_reminders_hours_before
                      }
                      onChange={handleChange}
                      className={`${hoursInputClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                    />
                    <span className="text-sm text-gray-600 dark:text-slate-400">
                      Stunden vorher
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        name="auto_cancel_unpaid_enabled"
                        checked={!!settings.auto_cancel_unpaid_enabled}
                        onChange={handleChange}
                      />
                      <div className={toggleTrackClass} />
                    </label>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      Unbezahlte Buchungen automatisch stornieren
                    </span>
                  </div>
                </div>
              </section>

              <section className="pt-2 border-t border-gray-200 dark:border-slate-600">
                <h3 className="text-base font-semibold text-gray-900 dark:text-slate-100 mb-4">
                  LLM-Bewertung
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 mr-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        LLM-Bewertung
                      </p>
                      <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                        KI-gestützte Buchungsbewertung aktivieren
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        name="llm_evaluation_enabled"
                        checked={!!settings.llm_evaluation_enabled}
                        onChange={handleChange}
                      />
                      <div className={toggleTrackClass} />
                    </label>
                  </div>

                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 mr-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Web-Plausibilitätsprüfung
                      </p>
                      <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                        Automatische Verifizierung über Websuche
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        name="web_plausibility_enabled"
                        checked={!!settings.web_plausibility_enabled}
                        onChange={handleChange}
                      />
                      <div className={toggleTrackClass} />
                    </label>
                  </div>

                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 mr-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Reputationsprüfung
                      </p>
                      <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                        Prüfung der Veranstalterreputation
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        name="reputation_check_enabled"
                        checked={!!settings.reputation_check_enabled}
                        onChange={handleChange}
                      />
                      <div className={toggleTrackClass} />
                    </label>
                  </div>
                </div>
              </section>

              <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-slate-600">
                <button
                  type="button"
                  onClick={() => loadSettings()}
                  className="px-4 py-2 text-rose-700 dark:text-rose-200 border border-rose-300 dark:border-rose-700 bg-rose-100 dark:bg-rose-900/30 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center px-4 py-2 bg-primary-color dark:bg-primary-color/40 text-white rounded-lg hover:bg-primary-color-dark dark:hover:bg-primary-color/40 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5 mr-2" />
                  )}
                  {saving ? "Speichern..." : "Einstellungen speichern"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomatisierungSettings;
